import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Dispatch, FC, SetStateAction } from 'react';
import type { DefaultPopupProps } from 'utils/props';

import Modal from 'components/Modal';
import ModalContentWrapper from 'components/ModalContentWrapper';

import PairDevicesSuccess from './PairDevicesSuccess';
import PairDevicesToRfids from './PairDevicesToRfids';
import UnPairDevicesFromRfids from './UnPairDevicesFromRfids';

type AssignDevicesToMembers = 'pairDevices' | 'unPairDevices' | 'success';

interface AssignDevicesToMembersModalState {
  stage: AssignDevicesToMembers;
  pairDevices: number[];
  newDeviceSerial?: string;
}

export interface AssignDevicesToMembersModalStageProps {
  open: boolean;
  state: AssignDevicesToMembersModalState;
  setState: Dispatch<SetStateAction<AssignDevicesToMembersModalState>>;
  onClose: () => void;
  onCloseProxy: () => void;
  selectedRfids: number[];
  pairedDevices: number[] | false;
}

const StageComponents: Record<
  AssignDevicesToMembers,
  FC<AssignDevicesToMembersModalStageProps>
> = {
  pairDevices: PairDevicesToRfids,
  unPairDevices: UnPairDevicesFromRfids,
  success: PairDevicesSuccess,
};

interface Props extends DefaultPopupProps {
  selectedRfids: number[];
  pairedDevices: number[] | false;
}

const AssignDevicesToMemberModal: FC<Props> = ({
  onClose,
  open,
  selectedRfids,
  pairedDevices,
}) => {
  const [state, setState] = useState<AssignDevicesToMembersModalState>({
    stage: 'pairDevices',
    pairDevices: [],
  });

  useEffect(() => {
    if (open) {
      setState(prev => ({
        ...prev,
        stage: pairedDevices === false ? 'pairDevices' : 'unPairDevices',
      }));
    }
  }, [pairedDevices, open, selectedRfids]);

  useEffect(() => {
    if (!open) {
      setState(prev => ({ ...prev, pairDevices: [] }));
    }
  }, [open]);

  const StageComponent = useMemo(() => StageComponents[state.stage], [state]);

  const onCloseProxy = useCallback((): void => {
    setState(prev => ({ ...prev, pairDevices: [], stage: 'unPairDevices' }));

    if (
      (state.stage === 'pairDevices' && pairedDevices === false) ||
      state.stage === 'unPairDevices'
    ) {
      onClose();
    }
  }, [pairedDevices, onClose, state.stage]);

  return (
    <Modal
      open={open}
      onClose={onCloseProxy}
      overrideCloseButtonIcon={
        state.stage === 'pairDevices' && pairedDevices ? (
          <ArrowBackRoundedIcon />
        ) : undefined
      }
      hideCloseButton={state.stage === 'success'}
    >
      <ModalContentWrapper>
        <StageComponent
          open={open}
          setState={setState}
          state={state}
          onClose={onClose}
          onCloseProxy={onCloseProxy}
          selectedRfids={selectedRfids}
          pairedDevices={pairedDevices}
        />
      </ModalContentWrapper>
    </Modal>
  );
};

export default AssignDevicesToMemberModal;
